/**
 * PaywallDemo → Admin Monetization Lab
 * Strict admin-only access. Includes paywall previews + Duo celebration simulation.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PaywallScreen, PaywallType, usePaywall } from "@/components/PaywallScreen";
import { ArrowLeft, Globe, Award, Camera, Users, Eye, Shield, Heart, Loader2, Sparkles, FlaskConical } from "lucide-react";
import { useAuth } from "@/App";
import DuoCelebration from "@/components/premium/DuoCelebration";

const ADMIN_EMAILS = ["admin@duolyst.com"];

export const PaywallDemo = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { paywallState, showPaywall, hidePaywall } = usePaywall();
  const [hasPartner, setHasPartner] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebHasDuo, setCelebHasDuo] = useState(true);

  // Admin guard — redirect instantly for non-admin
  const isAdmin = user && (
    user.founding_explorer ||
    user.is_admin ||
    ADMIN_EMAILS.includes(user.email)
  );

  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, isAdmin, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) return null;

  const paywalls = [
    {
      type: PaywallType.WORLD_MAP,
      name: "World Map Lock",
      description: "city heatmaps, world percentile, timeline replay, premium skins",
      icon: Globe,
      gradient: "from-blue-600 to-indigo-600",
      triggerSource: "city_heatmaps",
    },
    {
      type: PaywallType.TRAVEL_RANK,
      name: "Travel Rank Lock",
      description: "global percentile, advanced score, milestone forecast, rank history",
      icon: Award,
      gradient: "from-amber-500 to-rose-600",
      triggerSource: "rank_insights",
    },
    {
      type: PaywallType.MEMORY_VAULT,
      name: "Memory Vault Lock",
      description: "memory limit exceeded, scrapbook themes, anniversary vault",
      icon: Camera,
      gradient: "from-rose-500 to-fuchsia-600",
      triggerSource: "memory_limit_exceeded",
    },
    {
      type: PaywallType.DUO_UPSELL,
      name: "Duo Upsell",
      description: "partner link, first shared trip, couple milestone",
      icon: Users,
      gradient: "from-emerald-500 to-cyan-600",
      triggerSource: "partner_link",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-amber-500" />
            <h1 className="font-serif text-lg text-foreground">Monetization Lab</h1>
          </div>
          <div className="w-16" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Admin badge */}
        <div className="flex items-center gap-2 text-xs text-amber-500 bg-amber-500/10 px-3 py-2 rounded-lg w-fit" data-testid="admin-lab-badge">
          <Shield className="w-3 h-3" />
          Admin Only — {user.email}
        </div>

        {/* Duo Celebration Simulator */}
        <Card className="border-pink-500/20 bg-gradient-to-br from-pink-500/5 to-rose-500/5">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-pink-500" />
              Duo Celebration Simulator
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Preview the full Duo link celebration flow: confetti, welcome panel, push payload, and CTAs.
            </p>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={celebHasDuo} onChange={(e) => setCelebHasDuo(e.target.checked)} className="w-4 h-4 rounded" />
                Simulate Duo entitlement active
              </label>
            </div>
            <Button
              onClick={() => setShowCelebration(true)}
              className="bg-gradient-to-r from-pink-500 to-rose-500 hover:opacity-90 text-white"
              data-testid="simulate-celebration-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Preview Duo Celebration
            </Button>

            {/* Push payload preview */}
            <div className="mt-3 p-3 rounded-lg bg-muted/30 border border-border">
              <p className="text-xs font-medium text-foreground mb-1">Push Notification Payload:</p>
              <pre className="text-xs text-muted-foreground whitespace-pre-wrap">
{JSON.stringify({
  title: "You're now linked on DuoLyst",
  body: "Start building your shared world together.",
  data: { type: "partner_linked", link: "/dashboard" }
}, null, 2)}
              </pre>
            </div>
          </CardContent>
        </Card>

        {/* Partner toggle */}
        <div className="p-4 bg-card rounded-xl border border-border">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={hasPartner} onChange={(e) => setHasPartner(e.target.checked)} className="w-5 h-5 rounded border-border" />
            <span className="font-medium text-sm">Simulate user has partner linked</span>
            <span className="text-xs text-muted-foreground">(Shows Duo option in paywalls)</span>
          </label>
        </div>

        {/* Paywall cards */}
        <div className="grid gap-4 md:grid-cols-2">
          {paywalls.map((paywall) => (
            <div key={paywall.type} className="bg-card rounded-2xl border border-border overflow-hidden hover:shadow-lg transition-shadow">
              <div className={`bg-gradient-to-r ${paywall.gradient} p-5 text-white`}>
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                    <paywall.icon className="w-4 h-4" />
                  </div>
                  <h2 className="font-serif text-lg font-bold">{paywall.name}</h2>
                </div>
                <p className="text-white/80 text-xs">{paywall.description}</p>
              </div>
              <div className="p-3">
                <Button onClick={() => showPaywall(paywall.type, paywall.triggerSource)} className="w-full gap-2" data-testid={`preview-${paywall.type}`}>
                  <Eye className="w-4 h-4" />
                  Preview Paywall
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Info */}
        <Card>
          <CardContent className="p-5">
            <h3 className="font-semibold text-sm mb-2">A/B Testing Notes</h3>
            <ul className="text-xs text-muted-foreground space-y-1.5">
              <li>Each paywall has 3-4 copy variants (A, B, C, D)</li>
              <li>Variant assignment is hash-based for consistency</li>
              <li>All interactions tracked: impressions, dismissals, plan selection</li>
              <li>Trial eligibility checked in real-time</li>
              <li>Duo option only shows when user has linked partner</li>
            </ul>
          </CardContent>
        </Card>
      </main>

      {/* Paywall modal */}
      <PaywallScreen isOpen={paywallState.isOpen} onClose={hidePaywall} type={paywallState.type} triggerSource={paywallState.triggerSource} hasPartner={hasPartner} />

      {/* Celebration simulation overlay */}
      {showCelebration && (
        <DuoCelebration
          partnerName="Demo Partner"
          hasDuoAccess={celebHasDuo}
          onDismiss={() => setShowCelebration(false)}
          isSimulation={true}
        />
      )}
    </div>
  );
};

export default PaywallDemo;
